import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { logout } from "../services/authService";
import "./Sidebar.css";
import logo from "../assets/logo-melonares.png";
import { useNotificacionesData } from "../features/notificaciones/hooks/useNotificacionesData";
import { buildAlertsForPerson } from "../features/notificaciones/domain/buildAlertsForPersona";

import {
  IoPersonOutline,
  IoLocationOutline,
  IoCalendarOutline,
  IoNotificationsOutline,
  IoWarningOutline,
  IoLogOutOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoBedOutline,
  IoRestaurantOutline,
  IoWineOutline,
  IoFastFoodOutline,
} from "react-icons/io5";
import { GiCrossedPistols } from "react-icons/gi";

function Sidebar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Mismo criterio y misma fuente que la pantalla de Alertas: solo
  // Cazadores y Titulares de Arma cuentan para el badge de notificaciones.
  const { personas: personasConAlarma } = useNotificacionesData();
  const notifCount = useMemo(() => {
    const horizonDays = 60;
    let total = 0;
    for (const persona of personasConAlarma) {
      total += buildAlertsForPerson(persona, horizonDays).length;
    }
    return total;
  }, [personasConAlarma]);


  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Cerrar el menú si ensanchan la ventana a desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      {/* Botón hamburguesa SOLO en móvil */}
      <button
        className="nav-toggle"
        aria-label="Abrir menú"
        onClick={() => setOpen(true)}
      >
        <IoMenuOutline size={20} />
      </button>

      {/* Backdrop SOLO visible cuando el menú está abierto en móvil */}
      {open && <div className="sidebar-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`sidebar ${open ? "open" : ""}`}>
        {/* Cabecera móvil con botón cerrar (desktop permanece igual visualmente) */}
        <div className="sidebar-top">
          <div className="logo-container">
            <img src={logo} alt="Los Melonares" />
          </div>
          <button
            className="nav-close"
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
          >
            <IoCloseOutline size={18} />
          </button>
        </div>

        <div className="sidebar-content">
          <div className="section">
            <p className="section-title">Usuarios</p>
            <ul>
              <li className="sidebar-item">
                <NavLink to="/" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoCalendarOutline className="sidebar-icon" />
                  <span>Calendario</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/perfiles" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoPersonOutline className="sidebar-icon" />
                  <span>Perfiles</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/eventos" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoLocationOutline className="sidebar-icon" />
                  <span>Eventos</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink
                  to="/notificaciones"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={() => setOpen(false)}
                >
                  <div className="icon-badge-container">
                    <IoNotificationsOutline className="sidebar-icon" />
                    {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
                  </div>
                  <span>Alertas</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink
                  to="/alertas-titulares"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={() => setOpen(false)}
                >
                  <IoWarningOutline className="sidebar-icon" />
                  <span>Alertas Titulares</span>
                </NavLink>
              </li>
            </ul>
          </div>

          <div className="section">
            <p className="section-title">Base de datos</p>
            <ul>
              {/* <li className="sidebar-item">
                <NavLink to="/tipo-caceria" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoDocumentTextOutline className="sidebar-icon" />
                  <span>Tipo de cacería</span>
                </NavLink>
              </li> */}
              <li className="sidebar-item">
                <NavLink to="/cacerias" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoLocationOutline className="sidebar-icon" />
                  <span>Cacerías</span>
                </NavLink>
              </li>
              {/* <li className="sidebar-item">
                <NavLink to="/documentacion" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoDocumentOutline className="sidebar-icon" />
                  <span>Documentación</span>
                </NavLink>
              </li> */}
              <li className="sidebar-item">
                <NavLink to="/armas" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <GiCrossedPistols className="sidebar-icon" />
                  <span>Armas</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/habitaciones" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoBedOutline className="sidebar-icon" />
                  <span>Habitaciones</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/platos" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoRestaurantOutline className="sidebar-icon" />
                  <span>Platos</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/menus" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoFastFoodOutline className="sidebar-icon" />
                  <span>Menús</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/vinos" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoWineOutline className="sidebar-icon" />
                  <span>Vinos</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/alergenos" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoWarningOutline className="sidebar-icon" />
                  <span>Alérgenos</span>
                </NavLink>
              </li>
            </ul>
          </div>

          <div className="section">
            <p className="section-title">Sesión</p>
            <ul>
              {/* <li className="sidebar-item">
                <NavLink to="/accesos" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoKeyOutline className="sidebar-icon" />
                  <span>Accesos</span>
                </NavLink>
              </li>
              <li className="sidebar-item">
                <NavLink to="/contrasenas" className={({ isActive }) => (isActive ? "active" : "")} onClick={() => setOpen(false)}>
                  <IoLockClosedOutline className="sidebar-icon" />
                  <span>Contraseñas</span>
                </NavLink>
              </li> */}
              <li onClick={handleLogout} className="sidebar-item logout">
                <NavLink to="/login" onClick={() => setOpen(false)}>
                  <IoLogOutOutline className="sidebar-icon" />
                  <span>Cerrar sesión</span>
                </NavLink>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
